/**
 * Shared chrome for WhatsApp (waba) pages. Reuses Uni* primitives and
 * always shows stage-by-stage help via UniPageShell helpPage/helpApi.
 */
import React from 'react';
import { postJSON, putJSON } from '../../api/client.js';
import {
  FieldLabel, UniActionButton, UniConfirmModal, UniFormError, UniKpiCard, UniModal,
  UniPageShell, UniRowActions, UniSection, UniSpecEditModal, UniSpecViewModal,
  UniStatusPill, UniTable, UniTile, UniToolbar, uniSpecKeys, useUniCreate,
  useUniDelete, useUniEdit, useUniList, fmtCount,
} from '../uni/UniShared.jsx';

export const WABA_API = '/api/waba-chat';
export const WABA_V1 = '/api/waba-chat/v1';
export const THEME = 'waba';

export {
  UniActionButton, UniKpiCard, UniPageShell, UniSection, UniStatusPill,
  UniTable, UniTile, fmtCount, useUniList, useUniCreate,
};

export function wabaHeaders(phone) {
  if (!phone) return undefined;
  return { headers: { 'X-Waba-Phone': phone } };
}

function FieldInput({ field: f, draft, set }) {
  const id = React.useId();
  const control = (() => {
    if (f.type === 'textarea' || f.type === 'json') {
      return (
        <textarea
          id={id}
          rows={f.rows || (f.type === 'json' ? 6 : 4)}
          value={draft?.[f.key] ?? ''}
          onChange={(e) => set(f.key, e.target.value)}
          style={{ width: '100%', font: 'inherit' }}
        />
      );
    }
    if (f.type === 'check') {
      return (
        <input
          id={id}
          type="checkbox"
          checked={!!draft?.[f.key]}
          onChange={(e) => set(f.key, e.target.checked)}
        />
      );
    }
    if (f.type === 'select') {
      return (
        <select
          id={id}
          value={draft?.[f.key] ?? ''}
          onChange={(e) => set(f.key, e.target.value)}
          style={{ width: '100%', font: 'inherit', padding: 6 }}
        >
          <option value="">—</option>
          {(f.options || []).map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      );
    }
    if (f.type === 'password') {
      return (
        <input
          id={id}
          type="password"
          autoComplete="new-password"
          placeholder={f.secret_set ? '•••••••• (leave blank to keep)' : ''}
          value={draft?.[f.key] ?? ''}
          onChange={(e) => set(f.key, e.target.value)}
          style={{ width: '100%', font: 'inherit', padding: 6 }}
        />
      );
    }
    return (
      <input
        id={id}
        type={f.type === 'number' ? 'number' : 'text'}
        value={draft?.[f.key] ?? ''}
        onChange={(e) => set(f.key, e.target.value)}
        style={{ width: '100%', font: 'inherit', padding: 6 }}
      />
    );
  })();

  return (
    <div className="ty-field" style={{ fontSize: 13 }}>
      <FieldLabel htmlFor={id} label={f.label} required={!!f.required} help={f.help} />
      {f.hint && <div className="uni-num--muted" style={{ marginBottom: 4 }}>{f.hint}</div>}
      {control}
    </div>
  );
}

export function FieldInputs({ fields, draft, set }) {
  return (fields || []).filter((f) => f && f.editable !== false).map((f) => (
    <FieldInput key={f.key} field={f} draft={draft} set={set} />
  ));
}

export function SpecCreateModal({ create, title, fields, width }) {
  const list = (fields || []).filter((f) => f && f.editable !== false);
  const save = () => {
    const payload = {};
    // Hidden FKs (e.g. flow id on Bot Studio nodes) live on the draft
    // from initialCreate and must be posted even when not in the form.
    Object.entries(create.draft || {}).forEach(([k, v]) => {
      if (v !== '' && v !== undefined && v !== null) payload[k] = v;
    });
    list.forEach((f) => {
      let v = create.draft?.[f.key];
      if (f.type === 'json' && typeof v === 'string' && v.trim()) {
        try { v = JSON.parse(v); } catch { /* keep string */ }
      }
      if (f.type === 'number' && v !== '' && v != null) v = Number(v);
      if (f.type === 'check') payload[f.key] = !!v;
      else if (v !== '' && v !== undefined) payload[f.key] = v;
      else if (v === '') delete payload[f.key];
    });
    create.submit(payload);
  };
  return (
    <UniModal
      open={create.open}
      title={title}
      onClose={create.close}
      width={width}
      footer={(
        <>
          <button type="button" className="ty-btn ty-btn--secondary" onClick={create.close} disabled={create.busy}>Cancel</button>
          <button type="button" className="ty-btn" onClick={save} disabled={create.busy}>
            {create.busy ? 'Saving…' : 'Create'}
          </button>
        </>
      )}
    >
      <UniFormError error={create.error} />
      <FieldInputs fields={list} draft={create.draft} set={create.set} />
    </UniModal>
  );
}

export function CrudPage({
  theme, api, helpPage, icon, title, subtitle, home, endpoint, fields,
  columns, searchPlaceholder, createLabel, extraActions, extraRowActions,
  rowKey = 'id', empty, emptyHint, readOnly, initialCreate = {},
  helpOpen = true,
  aside,
  lead,
  tail,
  onCreated,
}) {
  const [q, setQ] = React.useState('');
  const sep = endpoint.includes('?') ? '&' : '?';
  const url = q && searchPlaceholder
    ? `${endpoint}${sep}search=${encodeURIComponent(q)}`
    : endpoint;
  const { rows, loading, error, raw, reload } = useUniList(url, [q]);
  const [viewing, setViewing] = React.useState(null);
  const collection = endpoint.split('?')[0];
  const base = collection.endsWith('/') ? collection : `${collection}/`;
  const edit = useUniEdit(base, () => reload());
  const del = useUniDelete(base, () => reload());
  const create = useUniCreate(base, initialCreate, (created) => {
    reload();
    onCreated?.(created);
  });
  const writable = fields && !readOnly;

  const actionCol = {
    key: 'actions',
    label: 'Actions',
    align: 'right',
    sortable: false,
    render: (r) => (
      <UniRowActions items={[
        ...(typeof extraRowActions === 'function' ? (extraRowActions(r) || []) : []),
        { key: 'view', label: 'View', onClick: () => setViewing(r) },
        writable && { key: 'edit', label: 'Edit', onClick: () => edit.start(r, uniSpecKeys(fields)) },
        writable && { key: 'delete', label: 'Delete', danger: true, onClick: () => del.ask(r) },
      ].filter(Boolean)} />
    ),
  };
  const cols = columns
    ? (columns.some((c) => c.key === 'actions') ? columns : [...columns, actionCol])
    : [
      { key: 'id', label: 'ID' },
      {
        key: 'name',
        label: 'Name',
        sortValue: (r) => r.name || r.title || r.phone_number || r.slug || r.keyword || r.id,
        render: (r) => r.name || r.title || r.phone_number || r.slug || r.keyword || r.id,
      },
      {
        key: 'status',
        label: 'Status',
        render: (r) => (r.status != null ? <UniStatusPill value={r.status} /> : '—'),
      },
      actionCol,
    ];

  return (
    <UniPageShell
      theme={theme}
      icon={icon}
      title={title}
      subtitle={subtitle || (raw?.count != null ? `${fmtCount(raw.count)} records` : undefined)}
      helpPage={helpPage}
      helpApi={api}
      helpOpen={helpOpen}
      actions={(
        <>
          {writable && (
            <UniActionButton hero primary onClick={create.start}>
              {createLabel || '+ New'}
            </UniActionButton>
          )}
          {extraActions}
          {home && <UniActionButton hero to={home}>← Home</UniActionButton>}
        </>
      )}
    >
      {lead}
      {searchPlaceholder && <UniToolbar onSearch={setQ} placeholder={searchPlaceholder} />}
      {aside ? (
        <div className="sb-wizard-with-aside">
          <div>
            <UniTable
              rows={rows} loading={loading} error={error} columns={cols} rowKey={rowKey}
              empty={empty || 'Nothing here yet'}
              emptyHint={emptyHint}
            />
          </div>
          {aside}
        </div>
      ) : (
        <UniTable
          rows={rows} loading={loading} error={error} columns={cols} rowKey={rowKey}
          empty={empty || 'Nothing here yet'}
          emptyHint={emptyHint}
        />
      )}
      {fields && (
        <UniSpecViewModal
          row={viewing}
          onClose={() => setViewing(null)}
          title={`${title} details`}
          fields={fields}
        />
      )}
      {writable && <UniSpecEditModal edit={edit} title={`Edit ${title.toLowerCase()}`} fields={fields} />}
      {writable && <SpecCreateModal create={create} title={createLabel || 'Create'} fields={fields} />}
      {writable && (
        <UniConfirmModal state={del} title={`Delete ${title.toLowerCase()}`} confirmLabel="Delete">
          <p style={{ margin: 0, fontSize: 13 }}>Delete this record? This cannot be undone.</p>
        </UniConfirmModal>
      )}
      {tail}
    </UniPageShell>
  );
}

export function HookTester({ api, helpPage, title, icon, theme, home }) {
  const catalog = useUniList(`${api}/hooks/catalog/`);
  const [hook, setHook] = React.useState('');
  const [payload, setPayload] = React.useState('{\n  \n}');
  const [out, setOut] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const hooks = catalog.raw?.hooks || [];

  const run = async () => {
    setBusy(true);
    setOut(null);
    try {
      let parsed = {};
      if (payload.trim()) parsed = JSON.parse(payload);
      const data = await postJSON(`${api}/hooks/inbound/`, { hook, payload: parsed });
      setOut(data);
    } catch (err) {
      setOut({ error: String(err.message || err) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <UniPageShell
      theme={theme}
      icon={icon}
      title={title}
      helpPage={helpPage}
      helpApi={api}
      subtitle="Incoming HTTP + in-process listeners. Outgoing twins are listed as on_* / send_whatsapp."
      actions={home ? <UniActionButton hero to={home}>← Home</UniActionButton> : null}
    >
      <UniTable
        rows={hooks}
        loading={catalog.loading}
        error={catalog.error}
        empty="No hooks registered"
        columns={[
          { key: 'name', label: 'Hook', render: (r) => <strong>{r.name}</strong> },
          { key: 'direction', label: 'Direction', render: (r) => <UniStatusPill value={r.direction} /> },
          { key: 'listeners', label: 'Listeners', align: 'right' },
          { key: 'help', label: 'Help' },
        ]}
        rowKey="name"
      />
      <div className="uni-section" style={{ marginTop: 18 }}>
        <h2 className="uni-section__title">POST inbound</h2>
      </div>
      <div style={{ display: 'grid', gap: 10, maxWidth: 640 }}>
        <label style={{ fontSize: 13 }}>
          Hook name
          <input
            value={hook}
            onChange={(e) => setHook(e.target.value)}
            list="hook-names"
            style={{ width: '100%', marginTop: 4, padding: 6 }}
          />
          <datalist id="hook-names">
            {hooks.map((h) => <option key={h.name} value={h.name} />)}
          </datalist>
        </label>
        <label style={{ fontSize: 13 }}>
          Payload JSON
          <textarea rows={8} value={payload} onChange={(e) => setPayload(e.target.value)} style={{ width: '100%', marginTop: 4 }} />
        </label>
        <button type="button" className="ty-btn" disabled={busy || !hook} onClick={run}>
          {busy ? 'Sending…' : 'Fire hook'}
        </button>
      </div>
      {out && (
        <pre className="uni-note" style={{ marginTop: 12, whiteSpace: 'pre-wrap', fontSize: 12 }}>
          {JSON.stringify(out, null, 2)}
        </pre>
      )}
    </UniPageShell>
  );
}

export function HelpIndexPage({ theme, api, helpPage, icon, title, home }) {
  const { raw, loading, error } = useUniList(`${api}/help/`);
  const pages = raw?.pages || {};
  return (
    <UniPageShell
      theme={theme} icon={icon} title={title}
      helpPage={helpPage} helpApi={api}
      actions={home ? <UniActionButton hero to={home}>← Home</UniActionButton> : null}
    >
      {loading && <div className="uni-state">Loading help…</div>}
      {error && <div className="ty-error">{String(error.message || error)}</div>}
      {Object.entries(pages).map(([key, item]) => (
        <article key={key} className="uni-note" style={{ marginBottom: 12 }}>
          <h3 style={{ margin: '0 0 6px' }}>{item.title} <span className="uni-num--muted">({key})</span></h3>
          <p style={{ margin: 0, fontSize: 13, lineHeight: 1.55 }}>{item.body}</p>
          {Array.isArray(item.steps) && item.steps.length > 0 && (
            <ol style={{ margin: '8px 0 0', paddingLeft: 18, fontSize: 13 }}>
              {item.steps.map((s, i) => <li key={i}>{s}</li>)}
            </ol>
          )}
        </article>
      ))}
    </UniPageShell>
  );
}

export function FeaturesPage({ theme, api, helpPage, icon, title, home }) {
  const { raw, loading, error } = useUniList(`${api}/features/`);
  const cats = raw?.categories || [];
  return (
    <UniPageShell
      theme={theme} icon={icon} title={title}
      helpPage={helpPage} helpApi={api}
      actions={home ? <UniActionButton hero to={home}>← Home</UniActionButton> : null}
    >
      {loading && <div className="uni-state">Loading…</div>}
      {error && <div className="ty-error">{String(error.message || error)}</div>}
      {cats.map((c) => (
        <section key={c.category} style={{ marginBottom: 16 }}>
          <h3>{c.category}</h3>
          <ul>{(c.items || []).map((item) => <li key={item}>{item}</li>)}</ul>
        </section>
      ))}
    </UniPageShell>
  );
}

export function SoloSettings({ theme, api, helpPage, icon, title, home, getUrl, fields }) {
  const { raw, loading, error, reload } = useUniList(getUrl);
  const [draft, setDraft] = React.useState({});
  const [busy, setBusy] = React.useState(false);
  const [msg, setMsg] = React.useState(null);
  React.useEffect(() => {
    if (raw && !Array.isArray(raw)) setDraft(raw);
  }, [raw]);
  const set = (k, v) => setDraft((d) => ({ ...d, [k]: v }));
  const save = async () => {
    setBusy(true);
    setMsg(null);
    try {
      await putJSON(getUrl, draft);
      setMsg('Saved.');
      reload();
    } catch (err) {
      setMsg(String(err.message || err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <UniPageShell
      theme={theme} icon={icon} title={title}
      helpPage={helpPage} helpApi={api}
      actions={home ? <UniActionButton hero to={home}>← Home</UniActionButton> : null}
    >
      {loading && <div className="uni-state">Loading…</div>}
      {error && <div className="ty-error">{String(error.message || error)}</div>}
      <FieldInputs fields={fields} draft={draft} set={set} />
      <button type="button" className="ty-btn" disabled={busy} onClick={save}>
        {busy ? 'Saving…' : 'Save'}
      </button>
      {msg && <p className="uni-note" style={{ marginTop: 10 }}>{msg}</p>}
    </UniPageShell>
  );
}

export function OrgSettingsForm({
  theme, api, helpPage, icon, title, subtitle, home, endpoint, extraActions,
}) {
  const { raw, loading, error, reload } = useUniList(endpoint);
  const [draft, setDraft] = React.useState({});
  const [busy, setBusy] = React.useState(false);
  const [msg, setMsg] = React.useState(null);
  const categories = raw?.categories || [];
  const notices = raw?.notices || [];

  React.useEffect(() => {
    const next = {};
    categories.forEach((c) => {
      (c.fields || []).forEach((f) => {
        next[f.key] = f.type === 'password' ? '' : (f.value ?? '');
      });
    });
    setDraft(next);
  }, [raw]);

  const set = (k, v) => setDraft((d) => ({ ...d, [k]: v }));
  const save = async () => {
    setBusy(true);
    setMsg(null);
    try {
      await putJSON(endpoint, { values: draft });
      setMsg('Saved for this organisation.');
      reload();
    } catch (err) {
      setMsg(String(err.message || err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <UniPageShell
      theme={theme}
      icon={icon}
      title={title}
      subtitle={subtitle}
      helpPage={helpPage}
      helpApi={api}
      actions={(
        <>
          {extraActions}
          {home && <UniActionButton hero to={home}>← Home</UniActionButton>}
        </>
      )}
    >
      {loading && <div className="uni-state">Loading…</div>}
      {error && <div className="ty-error">{String(error.message || error)}</div>}
      {notices.map((n) => (
        <p key={n} className="uni-help__copy" style={{ marginTop: 0 }}>{n}</p>
      ))}
      {categories.map((c) => (
        <section key={c.id} style={{ marginBottom: 22, maxWidth: 720 }}>
          <UniSection title={c.label} />
          <FieldInputs fields={c.fields || []} draft={draft} set={set} />
        </section>
      ))}
      <button type="button" className="ty-btn" disabled={busy || loading} onClick={save}>
        {busy ? 'Saving…' : 'Save'}
      </button>
      {msg && <p className="uni-note" style={{ marginTop: 10 }}>{msg}</p>}
    </UniPageShell>
  );
}
